import {
  BenefitType,
  PlanMarket,
  PolicyType,
  SelfEnrollType,
  UpdateSubmittedPlanDto,
} from '@zorro/clients';
import { VALIDATION_MESSAGES } from '@zorro/common-utils';
import { formatCurrencyEnUs } from '@zorro/shared/formatters';
import { PolicyTypeLabelConfig } from '@zorro/types';
import { getBenefitTypeLabel } from '@zorro/web/ui-utils';
import Decimal from 'decimal.js';
import { $enum } from 'ts-enum-util';
import * as yup from 'yup';

import { EnrollmentAllowanceStorePlan } from '../EnrollmentAllowanceCard';

export type PlanElectionSource = 'manual_election' | 'plan_election_drawer';

export const mapBenefitTypeToPlanElectionFlow = (benefitType: BenefitType) =>
  $enum.mapValue(benefitType).with<string | undefined>({
    [BenefitType.MAJOR_MEDICAL]: 'medical_plan_election',
    [BenefitType.SUPPLEMENTAL_DENTAL]: 'dental_plan_election',
    [BenefitType.SUPPLEMENTAL_VISION]: 'vision_plan_election',
    [BenefitType.SUPPLEMENTAL_COMBINED_DENTAL_VISION]:
      'combined_dental_vision_plan_election',
    [BenefitType.SUPPLEMENTAL_ACCIDENT]: undefined,
    [BenefitType.SUPPLEMENTAL_ACCIDENTAL_DEATH_AND_DISMEMBERMENT]: undefined,
    [BenefitType.SUPPLEMENTAL_CRITICAL_ILLNESS]: undefined,
    [BenefitType.SUPPLEMENTAL_FLEXIBLE_SPENDING_ACCOUNT]: undefined,
    [BenefitType.SUPPLEMENTAL_HOSPITAL]: undefined,
    [BenefitType.SUPPLEMENTAL_LIFE]: undefined,
    [BenefitType.SUPPLEMENTAL_OTHER]: undefined,
  });

export type PlanElectionItem = {
  id: string;
  name: string;
  premium: number;
  carrierName: string;
  hsaEligible: boolean | null;
  deductible?: number;
  maxOutOfPocket?: number;
  benefitsSummaryUrl?: string;
  planMarket?: PlanMarket | null;
  selfEnroll: SelfEnrollType;
  policyType?: PolicyType | null;
  extendedPlanDetails?: string | null;
  hasVisionRider?: boolean;
};

export const planElectionItemSchema = yup.object({
  whoIsEnrolled: yup
    .array()
    .of(yup.string().required())
    .min(1, VALIDATION_MESSAGES.whoIsEnrolledRequired)
    .required(VALIDATION_MESSAGES.whoIsEnrolledRequired),
  planId: yup.string(),
  carrier: yup.string().when('isMedicareCoverage', {
    is: false,
    then: (schema) => schema.required(VALIDATION_MESSAGES.carrierRequired),
    otherwise: (schema) => schema.notRequired(),
  }),
  planName: yup.string().required(VALIDATION_MESSAGES.planNameRequired),
  premium: yup
    .number()
    .min(0, VALIDATION_MESSAGES.premiumPositive)
    .required(VALIDATION_MESSAGES.premiumRequired),
  isHsaEligible: yup.boolean(),
  maxOutOfPocket: yup.number(),
  benefitsSummaryUrl: yup.string(),
  deductible: yup.number(),
  planMarket: yup
    .mixed<PlanMarket>()
    .oneOf(Object.values(PlanMarket))
    .nullable(),
  isSelfEnroll: yup.boolean().required(),
  isMedicareCoverage: yup.boolean().required(),
  hasVisionRider: yup.boolean(),
  medicareProvider: yup.string().when('isMedicareCoverage', {
    is: true,
    then: (schema) =>
      schema.required(VALIDATION_MESSAGES.medicareProviderRequired),
    otherwise: (schema) => schema.notRequired(),
  }),
  isCombinedPlanStructure: yup.boolean(),
  planType: yup
    .mixed<PolicyType>()
    .oneOf(Object.values(PolicyType))
    .when('isCombinedPlanStructure', {
      is: true,
      then: (schema) => schema.required(VALIDATION_MESSAGES.planTypeRequired),
      otherwise: (schema) => schema.notRequired(),
    }),
  combinedPlanInfo: yup.string().when('isCombinedPlanStructure', {
    is: true,
    then: (schema) =>
      schema.required(VALIDATION_MESSAGES.combinedPlanInfoRequired),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export type PlanElectionItemFormFields = yup.InferType<
  typeof planElectionItemSchema
>;
export const planElectionFormSchema = yup.object({
  plans: yup.array().of(planElectionItemSchema).required(),
});

export type PlanElectionFormFields = yup.InferType<
  typeof planElectionFormSchema
>;

export const formatExtendedPlanDetails = (
  plans: PlanElectionItemFormFields[],
  insuredIdToNameMap: Map<string, string>
): string | undefined => {
  return plans.length > 1
    ? plans
        .map(
          ({ premium, whoIsEnrolled }) =>
            `[${formatCurrencyEnUs(premium)}, ${whoIsEnrolled
              .map((id) => insuredIdToNameMap.get(id))
              .join(',')}]`
        )
        .join(' + ')
    : undefined;
};

export const policyTypeOptions = (): {
  value: PolicyType;
  label: string;
}[] =>
  Object.values(PolicyType).map((option) => ({
    value: option,
    label: PolicyTypeLabelConfig[option],
  }));

type Props = {
  isCombinedPlan: boolean;
  planIds: string;
  extendedPlanDetails: string | undefined;
  benefitsSummaryUrl: string | null;
  maxOutOfPocket: number | null;
  isHsaEligible: boolean | null;
  planName: string;
  carrierName: string;
  deductible: number | null;
  planMarket: PlanMarket | null;
  isSelfEnroll: boolean | undefined;
  policyType: PolicyType | undefined;
};

export const mapManualElectionFormToAddToCartPlan = (
  plans: PlanElectionItemFormFields[],
  isCombinedPlanStructure: boolean,
  computedExtendedPlanDetails: string | undefined
): Props => {
  const carrierName = plans
    .map((plan) =>
      plan.isMedicareCoverage ? plan.medicareProvider : plan.carrier
    )
    .join(' + ');
  const planName = plans.map((plan) => plan.planName).join(' + ');
  const planIds = plans
    .map((plan) => {
      if (plan.planId) {
        return plan.planId;
      }
      return plan.isMedicareCoverage ? PolicyType.MEDICARE : PolicyType.IFP;
    })
    .join(' + ');
  const maxOutOfPocket =
    plans.find((plan) => plan.maxOutOfPocket)?.maxOutOfPocket ?? null;
  // Matching on truthiness like the fields above would turn an explicit `false`
  // into `null`, so keep "not eligible" distinct from "never captured"
  const knownHsaEligibility = plans
    .map((plan) => plan.isHsaEligible)
    .filter((isEligible) => isEligible !== undefined);
  const isHsaEligible =
    knownHsaEligibility.length > 0 ? knownHsaEligibility.some(Boolean) : null;
  const benefitsSummaryUrl =
    plans.find((plan) => plan.benefitsSummaryUrl)?.benefitsSummaryUrl ?? null;
  const deductible = plans.find((plan) => plan.deductible)?.deductible ?? null;
  const planMarket =
    plans.find(({ planMarket }) => planMarket)?.planMarket ?? null;
  const isSelfEnroll = plans.find(
    ({ isSelfEnroll }) => isSelfEnroll
  )?.isSelfEnroll;

  const isAllMedicare = plans.every((plan) => plan.isMedicareCoverage);
  const isAllIFP = plans.every((plan) => !plan.isMedicareCoverage);
  const computedPlanType = isAllMedicare
    ? PolicyType.MEDICARE
    : isAllIFP
      ? PolicyType.IFP
      : PolicyType.MEDICARE_IFP;

  const isCombinedPlan = isCombinedPlanStructure || plans.length > 1;
  const isSingleCombinedPlan = plans.length === 1 && isCombinedPlanStructure;

  const policyType = isSingleCombinedPlan
    ? plans[0].planType
    : computedPlanType;

  const extendedPlanDetails = isSingleCombinedPlan
    ? plans[0].combinedPlanInfo
    : computedExtendedPlanDetails;
  return {
    isCombinedPlan,
    planIds,
    extendedPlanDetails,
    benefitsSummaryUrl,
    maxOutOfPocket,
    isHsaEligible,
    planName,
    carrierName,
    deductible,
    planMarket,
    isSelfEnroll,
    policyType,
  };
};

export const getWhoIsEnrolledInMultiplePlans = (
  allWhoIsEnrolledOptions: string[],
  plans: PlanElectionItemFormFields[]
) => {
  return allWhoIsEnrolledOptions.filter((userId) => {
    const plansWithUser = plans.filter((plan) =>
      plan.whoIsEnrolled.includes(userId)
    );
    return plansWithUser.length > 1;
  });
};

export const formatwhoIsEnrolledInMultiplePlans = (
  whoIsEnrolledInMultiplePlans: string[],
  insuredIdToNameMap: Map<string, string>,
  benefitType: BenefitType
) => {
  const whoIsEnrolledInMultiplePlansNames = whoIsEnrolledInMultiplePlans
    .map((enrolledId) => insuredIdToNameMap.get(enrolledId))
    .join(', ');

  return `Notice — ${whoIsEnrolledInMultiplePlansNames} is listed as covered on more than one ${getBenefitTypeLabel(benefitType)} plan`;
};

export const mapPlanElectionItemToEnrollmentAllowanceStorePlan = (
  plans: PlanElectionItemFormFields[]
): EnrollmentAllowanceStorePlan[] => {
  return plans.map(({ planId = '', premium, carrier: carrierName }) => ({
    planId,
    carrierName,
    premium: new Decimal(premium || 0).toNumber(),
  }));
};

export const mapPlanElectionItemToUpdateSubmittedPlanDto = (
  plans: PlanElectionItemFormFields[]
): UpdateSubmittedPlanDto => {
  const plan = plans[0];

  return {
    selfEnroll: plan.isSelfEnroll
      ? SelfEnrollType.SELF_ENROLL
      : SelfEnrollType.NOT_SELF_ENROLL,
    carrierName:
      (plan.isMedicareCoverage ? plan.medicareProvider : plan.carrier) ?? '',
    externalID: plan.planId ?? null,
    policyType: plan.planType,
    premium: plan.premium,
    isHsaEligible: plan.isHsaEligible ?? null,
    name: plan.planName,
    insuredPersonIds: plan.whoIsEnrolled,
    benefitsSummaryUrl: plan.benefitsSummaryUrl ?? null,
    // Only combined plans expose the "Combined plan info" input. Leave the
    // field out otherwise so the backend keeps the stored value untouched.
    extendedPlanDetails: plan.isCombinedPlanStructure
      ? (plan.combinedPlanInfo ?? null)
      : undefined,
  };
};
